import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mic2, Users, Newspaper, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "show", label: "Live Show", icon: Mic2 },
  { id: "collab", label: "Collab", icon: Users },
  { id: "media", label: "Media", icon: Newspaper },
  { id: "photo_video", label: "Photo/Video", icon: Camera },
];

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    inquiry_type: "show",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      return toast.error("All required fields must be filled.");
    }
    setLoading(true);
    try {
      await axios.post(`${API}/booking`, form);
      toast.success("Message sent. We'll respond within 48 hours.");
      setForm({ name: "", email: "", inquiry_type: "show", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 md:py-32 px-5 md:px-10 max-w-5xl mx-auto">
      <div className="text-center mb-12 reveal">
        <span className="font-mono-display text-[11px] uppercase tracking-[0.4em] text-[#FF00E5]">
          07 — Contact
        </span>
        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl mt-3 text-cream-soft">
          BOOK <span className="text-gradient-neon">C.A.F.</span>
        </h2>
        <p className="mt-4 text-white/60 text-sm md:text-base max-w-xl mx-auto">
          Live shows, collaborations, press, or photo/video work — send a clear brief and you&apos;ll hear back fast.
        </p>
        <a
          data-testid="booking-direct-email"
          href="mailto:Conceptandformalityllc@gmail.com"
          className="mt-4 inline-flex items-center gap-2 font-mono-display text-[11px] uppercase tracking-[0.25em] text-[#00F0FF] hover:text-glow-cyan transition-colors"
        >
          Or email direct &middot; Conceptandformalityllc@gmail.com
        </a>
      </div>

      <form data-testid="booking-form" onSubmit={submit} className="glass-card p-7 md:p-10 space-y-7 reveal">
        {/* Type selector */}
        <div>
          <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-3">
            Inquiry Type
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`booking-type-${t.id}`}
                onClick={() => setForm((f) => ({ ...f, inquiry_type: t.id }))}
                className={`relative px-3 py-3 border font-mono-display text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-all ${
                  form.inquiry_type === t.id
                    ? "border-[#FF00E5] text-[#FF00E5] glow-magenta bg-[#FF00E5]/5"
                    : "border-white/15 text-white/55 hover:border-white/40"
                }`}
              >
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
              Name <span className="text-[#FF00E5]">*</span>
            </label>
            <input
              data-testid="booking-input-name"
              type="text"
              required
              value={form.name}
              onChange={set("name")}
              className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
              Email <span className="text-[#FF00E5]">*</span>
            </label>
            <input
              data-testid="booking-input-email"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors"
              placeholder="you@company.com"
            />
          </div>
        </div>

        <div>
          <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
            Subject <span className="text-[#FF00E5]">*</span>
          </label>
          <input
            data-testid="booking-input-subject"
            type="text"
            required
            value={form.subject}
            onChange={set("subject")}
            className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30 transition-colors"
            placeholder="Brief headline of your inquiry"
          />
        </div>

        <div>
          <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
            Message <span className="text-[#FF00E5]">*</span>
          </label>
          <textarea
            data-testid="booking-input-message"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            className="w-full bg-black/40 border border-white/10 focus:border-[#FF00E5] outline-none px-4 py-3 text-cream-soft placeholder:text-white/30 transition-colors resize-none"
            placeholder="Date, venue, budget, vision — give us the details."
          />
        </div>

        <button
          type="submit"
          data-testid="booking-submit-button"
          disabled={loading}
          className="btn-shine w-full md:w-auto px-10 py-4 bg-gradient-to-r from-[#00F0FF] via-[#0099b3] to-[#00F0FF] bg-[length:200%_100%] hover:bg-[position:100%_0] text-black font-display font-bold text-xs uppercase tracking-[0.3em] disabled:opacity-60 disabled:cursor-not-allowed glow-cyan transition-all duration-500 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Transmitting…
            </>
          ) : (
            <>
              <Send size={14} /> Send Inquiry
            </>
          )}
        </button>
      </form>
    </section>
  );
}
